import isValid from 'date-fns/isValid';
import cloneDeep from 'lodash/cloneDeep';
import Assignees from '@/constants/Assignees';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import type ConstituentType from '@/constants/ConstituentType';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
import DateType from '@/constants/DateType';
import FilterOperator from '@/constants/forms/FilterOperator';
import type AdvancedConstituentSearchFilter from '@/types/AdvancedConstituentSearchFilter';
import type CustomField from '@/types/api/CustomField';
import { dateStringToDate } from '@/utils/dates';
import TemporaryFilterField from '../constants/TemporaryFilterField';
import type ConstituentFilterFormData from '../types/ConstituentFilterFormData';

const getPairedValue = <X, Y = X>(
  value: unknown,
  operator: FilterOperator,
  mapFunc: (x: X) => Y,
  validatorFunc: (x: unknown) => boolean,
): (Y | undefined)[] => {
  if (
    operator === FilterOperator.BETWEEN &&
    Array.isArray(value) &&
    value.every(validatorFunc)
  ) {
    return value.map(mapFunc);
  }
  if (!Array.isArray(value) && validatorFunc(value)) {
    if (operator === FilterOperator.GREATER_THAN) {
      return [mapFunc(value as X), undefined];
    }
    if (operator === FilterOperator.LESS_THAN) {
      return [undefined, mapFunc(value as X)];
    }
  }
  return [];
};

const validateDateValue = (value: unknown) =>
  typeof value === 'string' && isValid(dateStringToDate(value));

const mergeDefaultValues = (
  defaultValues: ConstituentFilterFormData,
  filters: AdvancedConstituentSearchFilter[] = [],
  customFields: CustomField[] = [],
): ConstituentFilterFormData => {
  const result = cloneDeep(defaultValues);

  customFields.forEach(({ customFieldId, fieldType }) => {
    result[TemporaryFilterField.CUSTOM_FIELDS][customFieldId] = { fieldType };
  });

  filters.forEach(({ field, isCustomField, operator, value }) => {
    switch (field) {
      case ConstituentSearchFilterField.NAME_EMAIL_PHONE:
      case ConstituentSearchFilterField.CITY:
        if (operator === FilterOperator.CONTAINS) {
          result[field] = value as string;
        }
        break;
      case ConstituentSearchFilterField.BIRTHDAY_MONTH:
        if (operator === FilterOperator.IS) {
          result[field] = value as number;
        }
        break;
      case ConstituentSearchFilterField.DOB:
        result[TemporaryFilterField.BORN_DATE_RANGE] = getPairedValue<
          string,
          Date
        >(value, operator, dateStringToDate, validateDateValue);
        break;
      case ConstituentSearchFilterField.DONATION_DATE:
        result[TemporaryFilterField.DONATION_DATE_RANGE] = getPairedValue<
          string,
          Date
        >(value, operator, dateStringToDate, validateDateValue);
        if (result[TemporaryFilterField.DONATION_DATE_RANGE].length) {
          result[ConstituentSearchFilterField.DONATION_DATE_TYPE] =
            DateType.CUSTOM_DATES;
        }
        break;
      case ConstituentSearchFilterField.DONATION_AMOUNT:
        result[TemporaryFilterField.DONATION_AMOUNT_RANGE] =
          getPairedValue<number>(
            value,
            operator,
            (x) => x / 100,
            (x) => typeof x === 'number',
          );
        break;
      case ConstituentSearchFilterField.DONATION_MINUTES:
        result[TemporaryFilterField.DONATION_HOURS_RANGE] =
          getPairedValue<number>(
            value,
            operator,
            (x) => x / 60,
            (x) => typeof x === 'number',
          );
        break;
      case ConstituentSearchFilterField.USER_IDS:
        if (
          operator === FilterOperator.ANY &&
          Array.isArray(value) &&
          value.length
        ) {
          const [userId] = value;
          result[TemporaryFilterField.ASSIGNEE] = userId;
        } else if (operator === FilterOperator.IS_NOT_NULL) {
          result[TemporaryFilterField.ASSIGNEE] = Assignees.ASSIGNED;
        } else if (operator === FilterOperator.IS_NULL) {
          result[TemporaryFilterField.ASSIGNEE] = Assignees.UNASSIGNED;
        }
        break;
      case ConstituentSearchFilterField.CONSTITUENT_TYPE:
        if (operator === FilterOperator.IS) {
          result[field] = value as ConstituentType;
        }
        break;
      case ConstituentSearchFilterField.CONSTITUENT_CATEGORIES:
      case ConstituentSearchFilterField.DONATION_TYPES:
      case ConstituentSearchFilterField.CAMPAIGN_IDS:
        if (Array.isArray(value) && operator === FilterOperator.ANY) {
          result[field] = value;
        }
        break;
      case ConstituentSearchFilterField.STATE:
      case ConstituentSearchFilterField.POSTAL_CODE:
        if (operator === FilterOperator.IS) {
          result[field] = value as string;
        }
        break;
      case ConstituentSearchFilterField.DONATION_DATE_TYPE:
        result[field] = value as DateType | '';
        break;
      default:
        if (isCustomField && field in result.customFields) {
          const customField = result.customFields[field];
          if (customField.fieldType === CustomFieldTypes.DATE_PICKER) {
            customField.value = getPairedValue<string, Date>(
              value,
              operator,
              dateStringToDate,
              validateDateValue,
            );
          } else if (customField.fieldType === CustomFieldTypes.CHECKBOX) {
            customField.value = operator === FilterOperator.IS_NOT_NULL;
          } else {
            customField.value = value as string | string[];
          }
        }
        break;
    }
  });

  return result;
};

export default mergeDefaultValues;
